import React, {useMemo, useState} from "react";
import {
    Box, TextField, MenuItem, Checkbox, ListSubheader, Typography,
    Grid, Slider, FormControl, InputLabel, FormHelperText,
    Accordion, AccordionSummary, AccordionDetails
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {inputTools} from "../InputTools/constant.js";
import {isMobile, parseJSON} from "../App/utils.js";
import {KBName} from "../names";
import {getGroupedModels} from "./Chat";
import {colorGreen} from "../App/theme";
import {Bolt} from "@mui/icons-material";
import {ChatModels} from "../constants";

export const renderImageGenerationModelsSelect = ({
                                                      groupedModels,
                                                      onChange,
                                                      model,
                                                      details = true,
                                                      noneOption,
                                                      style = {},
                                                      variant = "outlined",
                                                      withHeaders = true,
                                                  }) => {
    const formatPrice = (price) => {
        return `$${(price / 100000).toFixed(3)}`;
    };

    const renderValue = (selected) => {
        const selectedModel = Object.values(groupedModels)
            .flat()
            .find((m) => m.id === selected);
        let label = selectedModel ? (selectedModel.shortName || selectedModel.name) : noneOption || "";

        return (
            <Box position="relative" display="inline-block" width="100%">
                <Typography variant="body1"
                            align={!isMobile ? "left" : undefined}
                            pl={!isMobile ? 2 : 0}
                >{label}</Typography>
            </Box>
        );
    };

    return (
        <Box mt={2} sx={{ width: "100%", ...style }}>
            <TextField
                select
                value={model}
                onChange={(e) => {
                    const modelId = e.target.value;
                    let vendor = null;

                    Object.values(groupedModels).flat().forEach(model => {
                        if (model.id === modelId) {
                            vendor = model.vendor;
                        }
                    });

                    onChange(e.target.value, vendor);
                }}
                fullWidth
                variant={variant}
                label={details ? "Image Generation Model" : undefined}
                InputProps={
                    variant === "outlined"
                        ? {}
                        : {
                            disableUnderline: true,
                            style: { padding: 0 },
                        }
                }
                SelectProps={{
                    renderValue
                }}
            >
                {withHeaders && (
                    <ListSubheader style={{ paddingBottom: "8px", minWidth: '350px' }} key={"first"}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={6}>
                                <Typography variant="body2">Model</Typography>
                            </Grid>
                            <Grid item xs={5}>
                                <Typography variant="body2">Price per Request</Typography>
                            </Grid>
                            {details && (
                                <Grid item xs={1}>
                                    <Typography variant="body2">Service</Typography>
                                </Grid>
                            )}
                        </Grid>
                    </ListSubheader>
                )}

                {noneOption && (
                    <MenuItem value={""} key={"none"}>
                        <Typography variant="body1">{noneOption}</Typography>
                    </MenuItem>
                )}

                {Object.entries(groupedModels).map(
                    ([group, models]) => [
                        <ListSubheader key={group}>{group}</ListSubheader>,
                        models.map((myModel) => (
                            <MenuItem value={myModel.id} key={myModel.id}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={6} style={{ marginLeft: 6, marginRight: 6 }}>
                                        <Typography variant="body2">{myModel.shortName}</Typography>
                                    </Grid>
                                    <Grid item xs={5}>
                                        <Typography variant="body2">
                                            {formatPrice(myModel.pricePerRequest)}
                                        </Typography>
                                    </Grid>
                                    {details && (
                                        <Grid item xs={1}>
                                            <Typography variant="body2">
                                                {myModel.serviceDescription}
                                            </Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            </MenuItem>
                        )),
                    ]
                )}
            </TextField>
        </Box>
    );
};

export const renderChatModelsSelect = ({
                                           withoutObsolete,
                                           groupedModels,
                                           onChange,
                                           model,
                                           details = true,
                                           noneOption,
                                           style = {},
                                           variant = "outlined",
                                           withHeaders = true,
                                       }) => {
    const formatPrice = (price, n = 5) => {
        return `$${(price / (100 * 1000)).toFixed(n)}`;
    };
    const formatContextSize = (size) => `${size / 1000}K`;
    const precision = (size) => {
        if (size === "Large") return 3;
        if (size === "Medium") return 4;
        return 5;
    };

    const renderValue = (selected) => {
        const selectedModel = Object.values(groupedModels)
            .flat()
            .find((m) => m.id === selected);
        let label = selectedModel ? selectedModel.name : noneOption || "";

        return (
            <Box position="relative" display="inline-block" width="100%">
                <Typography
                    align={!isMobile ? "left" : undefined}
                    pl={!isMobile ? 2 : 0}
                    variant="body1">{label}</Typography>
                {selectedModel?.isOnPremises && (
                    <Typography
                        variant="caption"
                        color={colorGreen}
                        position="absolute"
                        bottom={-6}
                        right={-20}
                        style={{ fontSize: '0.45rem' }}
                    >
                        On-Premises
                    </Typography>
                )}
            </Box>
        );
    };

    return (
        <Box mt={2} sx={{ width: "100%", ...style }}>
            <TextField
                select
                value={model}
                onChange={(e) => {
                    const modelId = e.target.value;
                    let vendor = null;

                    // Iterate over all sizes dynamically
                    Object.keys(groupedModels).forEach(size => {
                        const models = groupedModels[size];
                        const model = models.find(m => m.id === modelId);
                        if (model) {
                            vendor = model.vendor;
                        }
                    });

                    onChange(e.target.value, vendor);
                }}
                fullWidth
                variant={variant}
                label={details ? "Chat Model" : undefined}
                InputProps={
                    variant === "outlined"
                        ? {}
                        : {
                            disableUnderline: true,
                            style: { padding: 0 },
                        }
                }
                SelectProps={{
                    renderValue
                }}
            >
                {withHeaders && (
                    <ListSubheader style={{ paddingBottom: "8px" }} key={"first"}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={5}>
                                <Typography variant="body2">Model</Typography>
                            </Grid>

                            <>
                                <Grid item xs={5}>
                                    <Typography variant="body2">input / output</Typography>
                                </Grid>
                                {details && (
                                    <Grid item xs={1}>
                                        <Typography variant="body2">Max Context</Typography>
                                    </Grid>
                                )}
                            </>
                        </Grid>
                    </ListSubheader>
                )}

                {noneOption && (
                    <MenuItem value={""} key={"none"}>
                        <Typography variant="body1">{noneOption}</Typography>
                    </MenuItem>
                )}

                {Object.entries(groupedModels).map(
                    ([size, models]) =>
                        (size !== "Obsolete" || !withoutObsolete) && [
                            <ListSubheader key={size}>{size}</ListSubheader>,
                            models
                                .filter((model) => !model?.obsolete || !withoutObsolete)
                                .filter((model) => !model?.hidden)
                                .map((myModel) => (
                                    <MenuItem value={myModel.id} key={myModel.id}>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid
                                                item
                                                xs={5}
                                                style={{ marginLeft: 0, marginRight: 20 }}
                                            >
                                                <Typography variant="body2">{myModel.name}
                                                    {myModel.instant ? <Bolt style={{fontSize: 12}} /> : ''}
                                                </Typography>
                                            </Grid>
                                            <>
                                                <Grid item xs={5}>
                                                    <Typography variant="body2">
                                                        {myModel.inputPrice
                                                            ? `${formatPrice(
                                                                myModel.inputPrice,
                                                                precision(myModel.size)
                                                            )} / ${formatPrice(
                                                                myModel.outputPrice,
                                                                precision(myModel.size)
                                                            )}`
                                                            : "On-premises model"}
                                                    </Typography>
                                                </Grid>
                                                {details && (
                                                    <Grid item xs={1}>
                                                        <Typography variant="body2">
                                                            {formatContextSize(myModel.context)}
                                                        </Typography>
                                                    </Grid>
                                                )}
                                            </>
                                        </Grid>
                                    </MenuItem>
                                )),
                        ]
                )}
            </TextField>
        </Box>
    );
};

const ChatSettings = ({KBData, setKBData, network}) => {
    const groupedModels = useMemo(getGroupedModels, []);
    const [advancedExpanded, setAdvancedExpanded] = useState(false);

    const formatPrice = (price, n = 5) => `$${(price / (100*1000)).toFixed(n)}`;

    const hasImageEditor = KBData?.inputTools?.map(o => parseJSON(o))?.some(o => o?.type === 'imageEditor')

    const handleChangeInputTools = (event) => {

        let inputToolsPerType = event.target.value.reduce((map, item) => {
            const type = parseJSON(item)?.type || inputTools?.[item]?.type || item;

            const existingItems = map.get(type) || [];
            if (type === "imageEditor") {
                // allow multiple tools per type (able to select more than one)
                return map.set(type, [...existingItems, item]);
            } else {
                // ensure unique tools per type (unable to select more than one of this type)
                return map.set(type, [item]);
            }
        }, new Map());

        let multipleInputToolsPerType = Array.from(inputToolsPerType.values()).flat();

        setKBData({
            ...KBData,
            inputTools: multipleInputToolsPerType
        });
    };

    // Get max thinking tokens for the selected model
    const maxThinkingTokens = ChatModels?.[KBData?.model]?.maxThinkingTokens;

    // Handler for the thinking tokens slider
    const handleThinkingTokensChange = (event, newValue) => {
        setKBData({
            ...KBData,
            options: {
                ...KBData.options,
                maxThinkingTokens: newValue
            }
        });
    };

    return (
        <Box mt={1}>
            <Box mt={2} sx={{ marginTop: '16px', width: '100%', marginBottom: '16px' }}>
                <TextField
                    inputProps={{
                        autoComplete: 'kb-title',
                        form: {
                            autoComplete: 'off',
                        },
                    }}
                    label={KBName + " Title"}
                    value={KBData.kbTitle}
                    onChange={(e) => setKBData({
                        ...KBData,
                        kbTitle: e.target.value
                    })}
                    fullWidth
                    required
                    variant="outlined"
                />
            </Box>

            <Box mt={2} sx={{ marginTop: '16px', width: '100%', marginBottom: '16px' }}>
                <TextField
                    label="Base Instructions"
                    value={KBData.kbInstructions}
                    onChange={(e) => setKBData({
                        ...KBData,
                        kbInstructions: e.target.value
                    })}
                    fullWidth
                    multiline
                    minRows={4}
                    variant="outlined"
                    inputProps={{ style: { maxHeight: '300px', overflow: 'auto' } }}
                />
            </Box>

            {KBData?.kbTitle && renderChatModelsSelect({
                groupedModels,
                onChange: (model) => setKBData({ ...KBData, model}),
                model: KBData?.model
            })}

            {/* Advanced Settings Accordion */}
            <Accordion
                expanded={advancedExpanded}
                onChange={() => setAdvancedExpanded(!advancedExpanded)}
                sx={{ mt: 3, mb: 2 }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="advanced-settings-content"
                    id="advanced-settings-header"
                >
                    <Typography variant="subtitle1">Chat Settings</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    {/* Thinking Tokens Slider - only show if maxThinkingTokens is defined for this model */}
                    {maxThinkingTokens && (
                        <Box sx={{ width: '100%', mb: 3 }}>
                            <FormControl fullWidth>
                                <Typography gutterBottom>
                                    Thinking Tokens: {KBData?.options?.maxThinkingTokens || 0}
                                </Typography>
                                <Slider
                                    value={KBData?.options?.maxThinkingTokens || 0}
                                    onChange={handleThinkingTokensChange}
                                    min={0}
                                    max={maxThinkingTokens}
                                    valueLabelDisplay="auto"
                                    aria-labelledby="thinking-tokens-slider"
                                />
                                <FormHelperText>
                                    Maximum tokens for AI thinking/reasoning before responding
                                </FormHelperText>
                            </FormControl>
                        </Box>
                    )}

                    {/* Messages Window Slider */}
                    <Box sx={{ width: '100%', mb: 3 }}>
                        <FormControl fullWidth>
                            <Typography gutterBottom>
                                Messages Window: {KBData?.options?.messagesWindow || 0}
                            </Typography>
                            <Slider
                                value={KBData?.options?.messagesWindow || 0}
                                onChange={(event, newValue) => setKBData({
                                    ...KBData,
                                    options: {
                                        ...KBData.options,
                                        messagesWindow: newValue
                                    }
                                })}
                                min={0}
                                max={100}
                                valueLabelDisplay="auto"
                                aria-labelledby="messages-window-slider"
                            />
                            <FormHelperText>
                                Maximum recent messages to retain in conversation (0 = no limit)
                            </FormHelperText>
                        </FormControl>
                    </Box>

                    {/* Tokens Window Slider */}
                    <Box sx={{ width: '100%' }}>
                        <FormControl fullWidth>
                            <Typography gutterBottom>
                                Tokens Window: {KBData?.options?.tokensWindow || 0}
                            </Typography>
                            <Slider
                                value={KBData?.options?.tokensWindow || 0}
                                onChange={(event, newValue) => setKBData({
                                    ...KBData,
                                    options: {
                                        ...KBData.options,
                                        tokensWindow: newValue
                                    }
                                })}
                                min={0}
                                max={200000}
                                step={1000}
                                valueLabelDisplay="auto"
                                aria-labelledby="tokens-window-slider"
                            />
                            <FormHelperText>
                                Maximum tokens to retain from recent messages (0 = no limit)
                            </FormHelperText>
                        </FormControl>
                    </Box>
                </AccordionDetails>
            </Accordion>

            <Box mt={2} sx={{ marginTop: '16px', width: '100%', marginBottom: '16px' }}>
                {/* Input tools selection - existing code remains unchanged */}
                <TextField
                    select
                    label="Tools"
                    value={KBData.inputTools || []}
                    onChange={handleChangeInputTools}
                    fullWidth
                    variant="outlined"
                    SelectProps={{
                        multiple: true,
                        renderValue: (selected) => (
                            <Box display="flex">
                                {selected.map((item, i) => {
                                    const itemJSONType = parseJSON(item)?.type;
                                    const key = itemJSONType ? itemJSONType : item;
                                    const props = {key:i + key, style: { marginRight: 10 }};
                                    return <div key={i}>{(inputTools?.[key]?.iconSettings?.(props) || inputTools?.[key]?.icon(props))}</div>;
                                })}
                            </Box>
                        )
                    }}
                >
                    {/* MenuItem content remains unchanged */}
                    {Object.keys(inputTools).filter(key => !inputTools[key].hidden).map(key => {
                        // Existing code for rendering menu items
                        const tool = inputTools[key];
                        const serviceName = tool?.service?.name;
                        const pricePerRequest = tool?.service?.pricePerRequest;
                        const pricePerMinute = tool?.service?.pricePerMinute;
                        const isImageEditor = key === 'imageEditor';

                        const icon = isImageEditor ? (tool?.iconSettings?.() || tool.icon()) : tool.icon();

                        let title = serviceName
                            ? (isMobile ? tool?.desc : serviceName + ' - ' + tool?.desc)
                            : tool.settingsLabel || tool.label

                        const label = isImageEditor
                            ? "Image editor" + (isMobile ? '' : ' - ' + tool?.desc)
                            : title;

                        let priceText = isImageEditor ? "" :
                            (pricePerRequest ? `${formatPrice(pricePerRequest, 4)}${!isMobile ? ' per request' : '/req'}` : '') +
                            (pricePerMinute ? `${formatPrice(pricePerMinute, 4)}${!isMobile ? ' per minute' : '/min'}` : '');

                        return (
                            <MenuItem value={isImageEditor ? JSON.stringify({ type: 'imageEditor', vendor: 'openkbs'}) : key} key={key}>
                                <Grid container alignItems="center" justifyContent="space-between">
                                    <Grid item xs={isMobile ? 8 : 4} container alignItems="center">
                                        <Checkbox
                                            icon={icon}
                                            checkedIcon={icon}
                                            checked={KBData?.inputTools?.indexOf(key) > -1 || !!KBData?.inputTools?.find(o => parseJSON(o)?.type === 'imageEditor')}
                                        />
                                        <Typography variant="body2">
                                            {label}
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={isMobile ? 4 : 4} style={{ textAlign: 'center' }}>
                                        <Box display="flex" justifyContent="flex-start" style={{ paddingLeft: isMobile ? '5%' : '25%' }}>
                                            <Typography variant="body2">
                                                {priceText}
                                            </Typography>
                                        </Box>
                                    </Grid>

                                    <Grid item xs={isMobile ? 0 : 4} style={{ textAlign: 'right' }}>
                                        {/* Placeholder */}
                                    </Grid>
                                </Grid>
                            </MenuItem>
                        );
                    })}
                </TextField>
            </Box>

        </Box>
    );
};

export default ChatSettings;